import React, { useState, useMemo } from "react";
import { clearUserData, getUserData } from "../utils/authStorage";

import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaLock, FaEye, FaEyeSlash, FaKey, FaTimes } from "react-icons/fa";
import "../styles/Profile.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// ─── helpers (unchanged) ────────────────────────────────────────────────────
const initials = (name) =>
  name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "??";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PK", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtStatus = (s) => {
  const map = { ACTIVE: "active", BLOCKED: "blocked", INACTIVE: "inactive" };
  return <span className={`status-badge ${map[s] || "inactive"}`}>{s}</span>;
};

const FieldRow = ({ icon, label, value, mono }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="profile-field-row">
      <div className="profile-field-icon">{icon}</div>
      <div className="profile-field-body">
        <p className="profile-field-label">{label}</p>
        <p className={`profile-field-value ${mono ? "mono" : ""}`}>{value}</p>
      </div>
    </div>
  );
};

const AccessPills = ({ access }) => {
  if (!access?.length) return null;
  return (
    <div className="profile-field-row">
      <div className="profile-field-icon">🔑</div>
      <div className="profile-field-body">
        <p className="profile-field-label">Access permissions</p>
        <div className="access-grid">
          {access.map((a) => (
            <span key={a} className="access-pill">
              {a.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="profile-section-card">
    <p className="profile-section-label">{title}</p>
    {children}
  </div>
);

// ─── password strength ───────────────────────────────────────────────────────
const getStrength = (password) => {
  if (!password) return { score: 0, label: "", color: "" };
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const isLong = password.length >= 12;
  const isMin = password.length >= 8;
  let score = 0;
  if (isMin) score++;
  if (isLong) score++;
  if (hasUpper) score++;
  if (hasLower) score++;
  if (hasNumber) score++;
  if (hasSpecial) score++;
  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial || !isMin)
    return { score, label: "Weak", color: "#e53e3e" };
  if (score <= 4) return { score, label: "Medium", color: "#dd6b20" };
  return { score, label: "Strong", color: "#38a169" };
};

const StrengthBar = ({ password }) => {
  const { score, label, color } = useMemo(
    () => getStrength(password),
    [password],
  );
  if (!password) return null;
  const filled = score <= 2 ? 1 : score <= 4 ? 2 : 3;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 4, height: 5 }}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              borderRadius: 3,
              background: i <= filled ? color : "#e2e8f0",
              transition: "background 0.3s",
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color, marginTop: 3 }}>
        {label}
      </div>
    </div>
  );
};

// ─── validation schema ───────────────────────────────────────────────────────
const changePasswordSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Must be at least 8 characters")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter")
    .matches(/[a-z]/, "Must contain at least one lowercase letter")
    .matches(/[0-9]/, "Must contain at least one number")
    .matches(/[^A-Za-z0-9]/, "Must contain at least one special character")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your password"),
});

// ─── password field component ────────────────────────────────────────────────
const PasswordInput = ({ name, placeholder, label }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="cpmodal-field">
      <label className="cpmodal-label">{label}</label>
      <div className="cpmodal-input-wrap">
        <span className="cpmodal-input-icon">
          <FaLock />
        </span>
        <Field
          type={show ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          className="cpmodal-input"
        />
        <span className="cpmodal-toggle" onClick={() => setShow((v) => !v)}>
          {show ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>
      <ErrorMessage name={name} component="div" className="cpmodal-error" />
    </div>
  );
};

// ─── change password modal ───────────────────────────────────────────────────
const ChangePasswordModal = ({ username, logout, navigate, onClose }) => {
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, resetForm },
  ) => {
   
  };

  return (
    <div className="cpmodal-overlay" onClick={onClose}>
      <div className="cpmodal-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cpmodal-header">
          <div className="cpmodal-header-left">
            <span className="cpmodal-header-icon">
              <FaKey />
            </span>
            <h3 className="cpmodal-title">Change Password</h3>
          </div>
          <button className="cpmodal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {successMsg ? (
          <div className="cpmodal-success">{successMsg}</div>
        ) : (
          <Formik
            initialValues={{ newPassword: "", confirmPassword: "" }}
            validationSchema={changePasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form className="cpmodal-form">
                <PasswordInput
                  name="newPassword"
                  label="New Password"
                  placeholder="Enter new password"
                />
                {/* strength bar tied to newPassword */}
                <StrengthBar password={values.newPassword} />

                <PasswordInput
                  name="confirmPassword"
                  label="Confirm New Password"
                  placeholder="Re-enter new password"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cpmodal-submit"
                >
                  {isSubmitting ? "Updating…" : "Update Password"}
                </button>
              </Form>
            )}
          </Formik>
        )}
      </div>

      {/* inline styles — avoids touching Profile.css */}
    </div>
  );
};

// ─── main Profile component ──────────────────────────────────────────────────
export default function Profile() {
  const { user } = getUserData();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { name, username, user_type, profile: p = {} } = user;
  const [showModal, setShowModal] = useState(false);
const ut = user_type; 
  const typeLabel = {
    "org": "Organization",
    "org-admin": "Org Admin",

    "user-maker": "User Maker",
    "user-checker": "User Checker",

    "branch-admin": "Branch User",
    "area-manager": "Area Manager",
    "rgm-user": "RGM User",
    "business-admin": "Business Admin",
  };

  const renderSections = () => {
    if (ut === "org")
      return (
        <>
          <Section title="Organization details">
            <FieldRow icon="🏦" label="Organization name" value={p.orgName} />
            <FieldRow icon="👤" label="Username" value={p.username} mono />
            <FieldRow icon="📞" label="Contact" value={p.contact} mono />
            <FieldRow icon="✉️" label="Email" value={p.email} />
            <FieldRow icon="🌐" label="Website" value={p.website} />
          </Section>
          <Section title="System info">
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );

    

    if (ut === "branch-admin")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />
            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
          </Section>
          <Section title="Branch details">
            <FieldRow icon="🏢" label="Branch name" value={p.branchName} />
            <FieldRow icon="🔢" label="Branch code" value={p.branchCode} mono />
            <FieldRow icon="📍" label="Address" value={p.branchAddress} />
            {/* <FieldRow icon="🆔" label="Branch ID" value={p.branchId} /> */}
          </Section>
          <Section title="Permissions & system">
            <AccessPills access={p.user_access} />
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );

    if (ut === "business-admin")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />
            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
          </Section>
          <Section title="Permissions & system">
            <AccessPills access={p.user_access} />
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
    if (ut === "user-checker")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />
            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
          </Section>
          <Section title="System">
            {/* <AccessPills access={p.user_access} /> */}
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
      if (ut === "user-maker")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />

            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
          </Section>
          <Section title="System">
            {/* <AccessPills access={p.user_access} /> */}
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
    if (ut === "area-manager")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />

            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
             <FieldRow icon="🏪" label="Area name" value={p.branchAddress} />
          </Section>
          <Section title="System">
            {/* <AccessPills access={p.user_access} /> */}
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
if (ut === "rgm-user")
      return (
        <>
          <Section title="Employee details">
            <FieldRow icon="👤" label="Employee name" value={p.employeeName} />
            <FieldRow
              icon="🔢"
              label="Employee number"
              value={p.employeeNumber}
              mono
            />
            <FieldRow icon="✉️" label="Employee Email" value={p.email} mono />

            <FieldRow icon="📞" label="Contact" value={p.employeeContact} mono />
             <FieldRow icon="🏪" label="Region name" value={p.branchAddress} />
          </Section>
          <Section title="System">
            {/* <AccessPills access={p.user_access} /> */}
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
    if (ut === "merchant" || ut === "outlet")
      return (
        <>
          <Section title="Merchant details">
            <FieldRow icon="🏪" label="Merchant name" value={p.merchantName} />
            <FieldRow icon="🏬" label="DBA name" value={p.dbaName} />
            <FieldRow icon="🆔" label="Merchant ID" value={p.merchantID} mono />
            <FieldRow icon="🏪" label="Store ID" value={p.storeId} mono />
            <FieldRow icon="💻" label="Terminal ID" value={p.terminalId} mono />
            <FieldRow
              icon="🏷️"
              label="Category code"
              value={p.merchantCategoryCode}
              mono
            />
            <div className="profile-field-row">
              <div className="profile-field-icon">🔴</div>
              <div className="profile-field-body">
                <p className="profile-field-label">Status</p>
                {fmtStatus(p.status)}
              </div>
            </div>
          </Section>
          <Section title="Banking details">
            <FieldRow icon="🏦" label="IBAN" value={p.iban} mono />
            <FieldRow icon="🏦" label="Bank BIC" value={p.bankBic} mono />
            <FieldRow icon="👤" label="Account title" value={p.accountTitle} />
          </Section>
          <Section title="Fee structure">
            <FieldRow
              icon="💰"
              label="Fee type"
              value={p.paymentDetails?.feeType === "F" ? "Fixed" : "Percentage"}
            />
            <FieldRow
              icon="🧾"
              label={
                p.paymentDetails?.feeType === "F"
                  ? "Fixed fee (PKR)"
                  : "Fee (%)"
              }
              value={p.paymentDetails?.feeValue}
            />
            {p.parentID ? (
              <FieldRow icon="🔗" label="Parent ID" value={p.parentID} mono />
            ) : null}
          </Section>
          <Section title="System info">
            <FieldRow icon="🗓️" label="Created" value={fmtDate(p.createdAt)} />
            <FieldRow
              icon="🔄"
              label="Last updated"
              value={fmtDate(p.updatedAt)}
            />
          </Section>
        </>
      );
  };

  return (
    <div className="profile-page">
      <div className="profile-header-card">
        <div className={"profile-card"}>
          <div className={`profile-avatar ${ut}`}>{initials(name)}</div>
          <div className="profile-header-info">
            <p className="profile-name">{name}</p>

            {/* username row with change password button */}
            <div className="profile-username-row">
              <p className="profile-username">{username}</p>
            </div>

            <span className={`profile-badge ${ut}`}>{typeLabel[ut] || ut}</span>
          </div>
        </div>
        {/* <div>
          <button className="btn-password" onClick={() => setShowModal(true)}>
            <FaKey style={{ fontSize: 11 }} />
            Change Password
          </button>
        </div> */}
      </div>

      {renderSections()}

      {showModal && (
        <ChangePasswordModal
          username={username}
          logout={logout}
          navigate={navigate}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
